import type { PollutionReason, SimEvent } from "../protocol/events";
import { findNode, mintCardInstance, type EngineState } from "./state";

/**
 * `02-cards-and-economy.md` §2.5: hazard cards are "shuffled into the player's deck when line
 * conditions or maintenance are neglected". This is the mechanism — the only
 * place the deck grows during a run.
 *
 * Two properties keep it fair rather than punishing:
 *
 *  - Each source pollutes exactly once. A pipe that leaks for ten ticks costs
 *    one card, not ten; the leak is already doing its own damage.
 *  - The card goes into the draw pile at a seeded random position, so it lands
 *    somewhere in the next few turns rather than immediately. Determinism
 *    holds because the position comes from the run's own RNG.
 */
export function pollute(
  state: EngineState,
  reason: PollutionReason,
  sourceId: string,
  events: SimEvent[],
): void {
  const cardId = state.stage.pollutionCards?.[reason];
  if (!cardId) return;

  const key = `${reason}:${sourceId}`;
  if (state.pollutionSources.includes(key)) return;
  state.pollutionSources.push(key);

  const instance = mintCardInstance(state, cardId);
  // Clogged Line's locked tile is fixed at the moment of injection, from the
  // sludge source's position right now — never looked up later, which is
  // what closes G-F7 (destroying that node afterwards can't shift a lock onto
  // an unrelated tile, since each instance's tile is independent).
  if (reason === "sludge") {
    const sourceNode = findNode(state, sourceId);
    if (sourceNode) {
      state.cloggedLineLockedTiles[instance.instanceId] = { ...sourceNode.pos };
    }
  }

  const index = state.rng.nextInt(state.drawPile.length + 1);
  state.drawPile.splice(index, 0, instance);

  events.push({ type: "deck.polluted", cardId, reason, sourceId });
}
